// src/ProjectMultiTagSelector.js
import React from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';

/**
 * Multi-select for linking additional projects to a task.
 * @param {Array<{id: number|string, name: string}>} projects - All available projects.
 * @param {number|string} primaryProjectId - Primary project ID to exclude.
 * @param {Array<number|string>} value - Selected additional project IDs.
 * @param {(newValue: Array<number|string>) => void} onChange - Callback on selection change.
 * @param {boolean} [disabled=false] - Disable selector.
 */
export default function ProjectMultiTagSelector({
  projects = [],
  primaryProjectId = '',
  value = [],
  onChange = () => {},
  disabled = false,
}) {
  const valueIds = value.map(String);

  // Sort options alphabetically by name, exclude primary
  const options = [...(projects || [])]
    .filter((p) => String(p.id) !== String(primaryProjectId))
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedOptions = options.filter((p) =>
    valueIds.includes(String(p.id))
  );

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selectedOptions}
      getOptionLabel={(option) => option.name || ''}
      onChange={(_, newValue) =>
        onChange(newValue.map((p) => p.id)) // Return IDs only
      }
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Also link to projects"
          placeholder={options.length > 0 ? "Select additional projects" : "No additional projects available"}
        />
      )}
      noOptionsText="No projects available"
      disabled={disabled}
    />
  );
}